// Система валидации медицинских данных
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface MedicalData {
  age?: number;
  weight?: number;
  height?: number;
  temperature?: number;
  dosage?: number;
  medication?: string;
  [key: string]: any;
}

// Базовые валидаторы
export const medicalValidators = {
  // Валидация возраста
  age: (age: number): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (age < 0) {
      errors.push('Возраст не может быть отрицательным');
      return { isValid: false, errors, warnings, severity: 'critical' };
    }
    
    if (age > 18) {
      warnings.push('Возраст превышает 18 лет - проверьте корректность данных');
    }
    
    if (age < 0.1) {
      warnings.push('Возраст менее 1 месяца - требуются особые расчеты');
    }
    
    return { 
      isValid: errors.length === 0, 
      errors, 
      warnings, 
      severity: errors.length > 0 ? 'critical' : warnings.length > 0 ? 'medium' : 'low' 
    };
  },

  // Валидация веса
  weight: (weight: number, age?: number): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (weight <= 0) {
      errors.push('Вес должен быть положительным числом');
      return { isValid: false, errors, warnings, severity: 'critical' };
    }
    
    if (weight > 200) {
      warnings.push('Вес превышает 200 кг - проверьте корректность данных');
    }
    
    // Проверка соответствия веса возрасту
    if (age && age < 1) {
      if (weight < 2) warnings.push('Вес менее 2 кг - возможны недоношенность или задержка развития');
      if (weight > 15) warnings.push('Вес более 15 кг для ребенка до года - проверьте данные');
    }
    
    return { 
      isValid: errors.length === 0, 
      errors, 
      warnings, 
      severity: errors.length > 0 ? 'critical' : warnings.length > 0 ? 'medium' : 'low' 
    };
  },

  // Валидация температуры
  temperature: (temp: number): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (temp < 35) {
      errors.push('Температура ниже 35°C - критическая гипотермия');
      return { isValid: false, errors, warnings, severity: 'critical' };
    }
    
    if (temp > 42) {
      errors.push('Температура выше 42°C - критическая гипертермия');
      return { isValid: false, errors, warnings, severity: 'critical' };
    }
    
    if (temp < 36) {
      warnings.push('Температура ниже 36°C - возможна гипотермия');
    }
    
    if (temp > 38) {
      warnings.push('Температура выше 38°C - лихорадка');
    }
    
    if (temp > 40) {
      warnings.push('Температура выше 40°C - высокая лихорадка, требует внимания');
    }
    
    return { 
      isValid: errors.length === 0, 
      errors, 
      warnings, 
      severity: errors.length > 0 ? 'critical' : warnings.length > 0 ? 'high' : 'low' 
    };
  },

  // Валидация дозировки
  dosage: (dose: number, maxDose: number, medication: string): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    if (dose === undefined || dose === null) {
      return { isValid: true, errors, warnings, severity: 'low' };
    }
    
    if (dose <= 0) {
      errors.push('Дозировка должна быть положительным числом');
      return { isValid: false, errors, warnings, severity: 'critical' };
    }
    
    if (dose > maxDose) {
      errors.push(`Дозировка ${dose} мг превышает максимальную дозу ${maxDose} мг для ${medication}`);
      return { isValid: false, errors, warnings, severity: 'critical' };
    }
    
    if (dose > maxDose * 0.8) {
      warnings.push(`Дозировка близка к максимальной (${Math.round((dose/maxDose)*100)}% от максимума)`);
    }
    
    return { 
      isValid: errors.length === 0, 
      errors, 
      warnings, 
      severity: errors.length > 0 ? 'critical' : warnings.length > 0 ? 'medium' : 'low' 
    };
  },

  // Комплексная валидация медицинских данных
  validateMedicalData: (data: MedicalData): ValidationResult => {
    const allErrors: string[] = [];
    const allWarnings: string[] = [];
    let maxSeverityLevel = 0; // 0=low, 1=medium, 2=high, 3=critical
    
    // Валидация возраста
    if (data.age !== undefined) {
      const ageResult = medicalValidators.age(data.age);
      allErrors.push(...ageResult.errors);
      allWarnings.push(...ageResult.warnings);
      const severityLevel = ageResult.severity === 'critical' ? 3 : 
                           ageResult.severity === 'high' ? 2 : 
                           ageResult.severity === 'medium' ? 1 : 0;
      maxSeverityLevel = Math.max(maxSeverityLevel, severityLevel);
    }
    
    // Валидация веса
    if (data.weight !== undefined) {
      const weightResult = medicalValidators.weight(data.weight, data.age);
      allErrors.push(...weightResult.errors);
      allWarnings.push(...weightResult.warnings);
      const severityLevel = weightResult.severity === 'critical' ? 3 : 
                           weightResult.severity === 'high' ? 2 : 
                           weightResult.severity === 'medium' ? 1 : 0;
      maxSeverityLevel = Math.max(maxSeverityLevel, severityLevel);
    }
    
    // Валидация температуры
    if (data.temperature !== undefined) {
      const tempResult = medicalValidators.temperature(data.temperature);
      allErrors.push(...tempResult.errors);
      allWarnings.push(...tempResult.warnings);
      const severityLevel = tempResult.severity === 'critical' ? 3 : 
                           tempResult.severity === 'high' ? 2 : 
                           tempResult.severity === 'medium' ? 1 : 0;
      maxSeverityLevel = Math.max(maxSeverityLevel, severityLevel);
    }
    
    // Валидация дозировки
    if (data.dosage !== undefined && data.dosage !== null && data.medication) {
      const maxDose = getMaxDoseForMedication(data.medication, data.age, data.weight);
      if (maxDose > 0) {
        const doseResult = medicalValidators.dosage(data.dosage, maxDose, data.medication);
        allErrors.push(...doseResult.errors);
        allWarnings.push(...doseResult.warnings);
        const severityLevel = doseResult.severity === 'critical' ? 3 : 
                             doseResult.severity === 'high' ? 2 : 
                             doseResult.severity === 'medium' ? 1 : 0;
        maxSeverityLevel = Math.max(maxSeverityLevel, severityLevel);
      }
    }
    
    const finalSeverity = maxSeverityLevel === 3 ? 'critical' : 
                         maxSeverityLevel === 2 ? 'high' : 
                         maxSeverityLevel === 1 ? 'medium' : 'low';
    
    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
      severity: finalSeverity
    };
  }
};

// Получение максимальной дозы для препарата
function getMaxDoseForMedication(medication: string, age?: number, weight?: number): number {
  const maxDoses: { [key: string]: number } = {
    'paracetamol': 60, // мг/кг/день
    'ibuprofen': 40,   // мг/кг/день
    'amoxicillin': 90, // мг/кг/день
    'azithromycin': 10, // мг/кг/день
    'cetirizine': 10,  // мг/день
    'levocetirizine': 5, // мг/день
    'salbutamol': 0.5, // мг/кг/доза
    'furosemide': 2    // мг/кг/доза
  };
  
  const baseMaxDose = maxDoses[medication.toLowerCase()] || 0;
  
  // Корректировка по возрасту
  if (age && age < 2) {
    return baseMaxDose * 0.5; // Снижение дозы для детей до 2 лет
  }
  
  return baseMaxDose;
}

// Цвета для отображения серьезности
export const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical': return '#FF1744';
    case 'high': return '#FF9800';
    case 'medium': return '#FFC107';
    case 'low': return '#4CAF50';
    default: return '#9E9E9E';
  }
};

// Иконки для отображения серьезности
export const getSeverityIcon = (severity: string): string => {
  switch (severity) {
    case 'critical': return '🚨';
    case 'high': return '⚠️';
    case 'medium': return '⚡';
    case 'low': return '✅';
    default: return 'ℹ️';
  }
};
